import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ACCESS_TOKEN_KEY, AUTH_STATE_KEY, tokenStorage } from '@/api/client';
import { authApi, userApi } from '@/api/endpoints';
import type { User } from '@/api/types';

interface AuthState {
  user: User | null;
  initialized: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** 소셜 로그인 콜백에서 받은 토큰으로 세션을 세운다. */
  loginWithTokens: (accessToken: string, refreshToken: string) => Promise<void>;
  logout: () => Promise<void>;
  /** 새로고침 시 저장된 토큰으로 사용자 정보를 복원한다. */
  restore: () => Promise<void>;
  setUser: (user: User) => void;
  isManager: () => boolean;
  isAdmin: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      initialized: false,

      async login(email, password) {
        const token = await authApi.login({ email, password });
        tokenStorage.save(token.accessToken, token.refreshToken);
        set({ user: token.user, initialized: true });
      },

      async loginWithTokens(accessToken, refreshToken) {
        tokenStorage.save(accessToken, refreshToken);
        const user = await userApi.me();
        set({ user, initialized: true });
      },

      async logout() {
        const refreshToken = tokenStorage.getRefreshToken();
        try {
          if (refreshToken) {
            await authApi.logout(refreshToken);
          }
        } finally {
          tokenStorage.clear();
          set({ user: null });
        }
      },

      async restore() {
        if (!tokenStorage.getAccessToken()) {
          set({ user: null, initialized: true });
          return;
        }
        try {
          const user = await userApi.me();
          set({ user, initialized: true });
        } catch {
          tokenStorage.clear();
          set({ user: null, initialized: true });
        }
      },

      setUser: (user) => set({ user }),

      isManager: () => {
        const role = get().user?.role;
        return role === 'ROLE_ADMIN' || role === 'ROLE_MANAGER';
      },

      isAdmin: () => get().user?.role === 'ROLE_ADMIN',
    }),
    {
      name: AUTH_STATE_KEY,
      // 토큰은 tokenStorage 가 관리하므로 사용자 정보만 저장한다.
      partialize: (state) => ({ user: state.user }),
    },
  ),
);

// 다른 탭에서 로그인/로그아웃/회원가입하면 토큰만 바뀌고 이 탭의 user 는 그대로 남는다.
// 그 상태로 API 를 호출하면 "화면은 관리자, 토큰은 다른 계정" 이 되어 403 이 난다.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === ACCESS_TOKEN_KEY) {
      void useAuthStore.getState().restore();
    }
  });
}
